import React from 'react';
import { FileText, Download, Play } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function resolveUrl(url) {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${API_URL}${url}`;
}

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDuration(seconds) {
  if (!seconds) return '';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export default function MediaRenderer({ messageType, fileUrl, fileName, fileSize, duration, isOwn }) {
  const url = resolveUrl(fileUrl);
  if (!url) return null;

  const textColor = isOwn ? 'text-white/80' : 'text-stone-500';

  if (messageType === 'voice' || messageType === 'audio') {
    return (
      <div className="space-y-1" data-testid="media-audio-player">
        <audio controls className="max-w-full" style={{ height: '36px' }} preload="metadata">
          <source src={url} />
        </audio>
        {duration > 0 && (
          <p className={`text-xs ${textColor}`}>{formatDuration(duration)}</p>
        )}
      </div>
    );
  }

  if (messageType === 'video') {
    return (
      <div className="space-y-1" data-testid="media-video-player">
        <video
          controls
          preload="metadata"
          className="rounded-lg max-w-full"
          style={{ maxHeight: '300px' }}
        >
          <source src={url} />
        </video>
        {fileName && (
          <p className={`text-xs ${textColor}`}>
            {fileName} {fileSize ? `(${formatFileSize(fileSize)})` : ''}
          </p>
        )}
      </div>
    );
  }

  if (messageType === 'image') {
    return (
      <div className="space-y-1" data-testid="media-image-preview">
        <img
          src={url}
          alt={fileName || 'Image'}
          className="rounded-lg max-w-full cursor-pointer"
          style={{ maxHeight: '300px' }}
          onClick={() => window.open(url, '_blank')}
        />
        {fileName && (
          <p className={`text-xs ${textColor}`}>
            {fileName} {fileSize ? `(${formatFileSize(fileSize)})` : ''}
          </p>
        )}
      </div>
    );
  }

  if (messageType === 'file' || messageType === 'document') {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
          isOwn ? 'bg-white/10 hover:bg-white/20' : 'bg-stone-50 hover:bg-stone-100'
        }`}
        data-testid="media-file-link"
      >
        <FileText className={`w-8 h-8 flex-shrink-0 ${isOwn ? 'text-white/80' : 'text-purple-500'}`} />
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium truncate ${isOwn ? 'text-white' : 'text-stone-900'}`}>
            {fileName || 'File'}
          </p>
          {fileSize > 0 && (
            <p className={`text-xs ${textColor}`}>{formatFileSize(fileSize)}</p>
          )}
        </div>
        <Download className={`w-4 h-4 flex-shrink-0 ${isOwn ? 'text-white/60' : 'text-stone-400'}`} />
      </a>
    );
  }

  return null;
}
